/**
 * Skybox v2 (R2 §6.1) — the original's planetary soul, procedurally:
 * two-layer parallax starfield (far static, near drifting with z + a few
 * degrees of camera-x parallax), a per-world nebula gradient built from the
 * palette's two sky tones, and one hero body with a proper terminator:
 *   world 1 — ringed cyan gas giant low on the horizon
 *   world 2 — huge dim red sun with faint corona
 *   world 3 — banded gas giant with two small moons
 * Bodies sit near the horizon and frame the road; the beat-pulse horizon
 * ring renders in front (it stays the signature). Reduced motion: starfield
 * drift only, no twinkle. All shader — zero texture downloads.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { WorldPalette } from "../config/tuning";

Effect.ShadersStore["skypulseSkyVertexShader"] = `
precision highp float;
attribute vec3 position;
uniform mat4 worldViewProjection;
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}`;

Effect.ShadersStore["skypulseSkyFragmentShader"] = `
precision highp float;
varying vec3 vDir;
uniform vec3 uSkyA;
uniform vec3 uSkyB;
uniform vec3 uBodyTint;
uniform vec3 uBodyDir;
uniform float uBodyRadius;
uniform float uType;      // 1 ringed giant · 2 red sun · 3 banded giant + moons
uniform float uTime;
uniform float uDriftZ;    // travel-linked drift for the near star layer
uniform float uCamX;      // slight lateral parallax
uniform float uReduced;   // 1 = reduced motion (no twinkle)

float hash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float starLayer(vec3 nd, float scale, float density, float drift, float par) {
  vec3 d = nd;
  d.x += par;
  d.z += drift;
  vec3 cell = floor(d * scale);
  float h = hash(cell);
  if (h < 1.0 - density) return 0.0;
  vec3 f = fract(d * scale) - 0.5;
  float m = smoothstep(0.32, 0.02, length(f));
  float tw = uReduced > 0.5 ? 0.85 : (0.7 + 0.3 * sin(uTime * 2.4 + h * 43.0));
  return m * tw * (0.5 + 0.5 * h);
}

void main() {
  vec3 nd = normalize(vDir);

  // --- nebula gradient: sky tones pool near the horizon, space above ---
  float hor = exp(-abs(nd.y) * 3.2);
  vec3 neb = mix(uSkyB, uSkyA, clamp(nd.y * 2.2 + 0.55, 0.0, 1.0));
  neb += uSkyB * 0.25 * sin(nd.x * 2.7 + 1.3) * hor;
  vec3 col = vec3(0.012, 0.012, 0.035) + neb * hor;

  // --- stars: far static + near drifting layer ---
  float sFar = starLayer(nd, 90.0, 0.03, 0.0, 0.0);
  float sNear = starLayer(nd, 42.0, 0.05, uDriftZ, uCamX * 0.012);
  col += vec3(0.9, 0.95, 1.0) * (sFar * 0.8 + sNear * 0.55) * (1.0 - hor * 0.45);

  // --- hero body ---
  vec3 bd = normalize(uBodyDir);
  vec3 off = nd - bd;
  float d = length(off);
  float disc = smoothstep(uBodyRadius, uBodyRadius * 0.985, d);
  // light leans toward the viewer (+z) so the facing hemisphere is lit and
  // the terminator falls across the visible disc
  vec3 lightDir = normalize(vec3(-0.5, 0.45, 0.55));
  float shade = clamp(dot(normalize(off + bd * 0.15), lightDir) * 0.9 + 0.45, 0.06, 1.0);
  vec3 bodyCol = uBodyTint;

  if (uType < 1.5) {
    // ringed cyan gas giant: latitude bands + ellipse ring
    float band = 0.85 + 0.15 * sin((off.y / uBodyRadius) * 9.0 + 1.0);
    bodyCol *= band;
    float er = length(vec2(off.x, off.y * 3.4 + off.x * 0.55));
    float ring = smoothstep(uBodyRadius * 2.15, uBodyRadius * 1.85, er) *
                 smoothstep(uBodyRadius * 1.25, uBodyRadius * 1.5, er);
    col += uBodyTint * ring * 0.5 * (1.0 - disc);
  } else if (uType < 2.5) {
    // huge dim red sun: soft corona, faint limb darkening, no terminator
    shade = 1.0 - smoothstep(0.0, uBodyRadius, d) * 0.35;
    col += uBodyTint * exp(-d * 9.0) * 0.55 * (1.0 - disc);
  } else {
    // banded giant + two moons
    float band = 0.8 + 0.2 * sin((off.y / uBodyRadius) * 12.0 + 0.6);
    bodyCol *= band;
    float m1 = smoothstep(0.022, 0.019, length(nd - normalize(bd + vec3(0.16, 0.05, 0.0))));
    float m2 = smoothstep(0.014, 0.012, length(nd - normalize(bd + vec3(-0.12, 0.09, 0.0))));
    col = mix(col, vec3(0.75, 0.78, 0.85) * shade, max(m1, m2));
  }

  col = mix(col, bodyCol * shade, disc);
  gl_FragColor = vec4(col, 1.0);
}`;

const BODY_SETUPS: Record<number, { dir: [number, number, number]; radius: number; type: number; tint: string }> = {
  1: { dir: [0.24, 0.1, 1], radius: 0.13, type: 1, tint: "#2E8FA8" },
  2: { dir: [-0.2, 0.12, 1], radius: 0.24, type: 2, tint: "#7A1E22" },
  3: { dir: [0.2, 0.11, 1], radius: 0.15, type: 3, tint: "#3D6B8C" },
};

export class SkyboxFx {
  private mesh: Mesh;
  private mat: ShaderMaterial;
  private reduced: boolean;

  constructor(scene: Scene, palette: WorldPalette, world: number, reducedMotion: boolean) {
    this.reduced = reducedMotion;
    this.mesh = CreateBox("skybox", { size: 700, sideOrientation: Mesh.BACKSIDE }, scene);
    this.mesh.infiniteDistance = true;
    this.mesh.isPickable = false;
    this.mat = new ShaderMaterial("mat-sky", scene, "skypulseSky", {
      attributes: ["position"],
      uniforms: [
        "worldViewProjection", "uSkyA", "uSkyB", "uBodyTint", "uBodyDir",
        "uBodyRadius", "uType", "uTime", "uDriftZ", "uCamX", "uReduced",
      ],
    });
    this.mat.disableDepthWrite = true;
    this.mat.backFaceCulling = false;
    const setup = BODY_SETUPS[world] ?? BODY_SETUPS[1]!;
    this.mat.setColor3("uSkyA", Color3.FromHexString(palette.skyPrimary));
    this.mat.setColor3("uSkyB", Color3.FromHexString(palette.skySecondary));
    this.mat.setColor3("uBodyTint", Color3.FromHexString(setup.tint));
    this.mat.setVector3("uBodyDir", new Vector3(setup.dir[0], setup.dir[1], setup.dir[2]));
    this.mat.setFloat("uBodyRadius", setup.radius);
    this.mat.setFloat("uType", setup.type);
    this.mat.setFloat("uReduced", reducedMotion ? 1 : 0);
    this.mat.setFloat("uTime", 0);
    this.mat.setFloat("uDriftZ", 0);
    this.mat.setFloat("uCamX", 0);
    this.mesh.material = this.mat;
  }

  update(timeS: number, camX: number, shipZ: number): void {
    this.mat.setFloat("uTime", timeS);
    this.mat.setFloat("uCamX", camX);
    // near-layer drift: a few degrees over a whole run — slow parallax depth
    this.mat.setFloat("uDriftZ", this.reduced ? 0 : shipZ * 0.00022);
  }

  setReducedMotion(on: boolean): void {
    this.reduced = on;
    this.mat.setFloat("uReduced", on ? 1 : 0);
  }

  dispose(): void {
    this.mat.dispose();
    this.mesh.dispose();
  }
}
